import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Fade,
  Paper,
  styled,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Language as LanguageIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { Language } from '../../i18n/translations';

interface ThemeLanguageSwitcherProps {
  darkMode: boolean;
  onToggleTheme: () => void;
}

const SwitcherContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  
  // Mobile positioning (top right)
  [theme.breakpoints.down('sm')]: {
    top: 0,
    right: 0,
  },
  
  // Desktop positioning (bottom right)
  [theme.breakpoints.up('sm')]: {
    bottom: 24,
    right: 24,
  },
}));

const LanguageOptions = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  background: theme.palette.mode === 'dark' 
    ? 'rgba(33, 33, 33, 0.95)' 
    : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  border: `1px solid ${theme.palette.divider}`,
  
  // Mobile positioning
  [theme.breakpoints.down('sm')]: {
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1),
    position: 'absolute',
    top: '100%',
    right: 0,
  },
  
  // Desktop positioning
  [theme.breakpoints.up('sm')]: {
    marginRight: 0,
    marginBottom: theme.spacing(1),
  },
}));

const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  background: theme.palette.mode === 'dark' 
    ? 'rgba(33, 33, 33, 0.9)' 
    : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(0.5),
  
  // Mobile styling (top right with rounded bottom left)
  [theme.breakpoints.down('sm')]: {
    borderRadius: '0 0 0 12px',
    borderTop: 'none',
    borderRight: 'none',
  },
  
  // Desktop styling (bottom right with rounded corners)
  [theme.breakpoints.up('sm')]: {
    borderRadius: 24,
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  transition: 'all 0.2s ease',
  padding: theme.spacing(0.75),
  '&:hover': {
    transform: 'scale(1.1)',
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
  },
  
  // Make buttons smaller on mobile
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
  },
}));

const ThemeLanguageSwitcher: React.FC<ThemeLanguageSwitcherProps> = ({
  darkMode,
  onToggleTheme,
}) => {
  const [showLanguageOptions, setShowLanguageOptions] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleLanguageClick = () => {
    setShowLanguageOptions(!showLanguageOptions);
  };

  const handleLanguageSelect = (lang: Language) => {
    setLanguage(lang);
    setShowLanguageOptions(false);
  };

  return (
    <SwitcherContainer>
      <Fade in={showLanguageOptions}>
        <LanguageOptions>
          <Tooltip title="English" arrow>
            <IconButton 
              onClick={() => handleLanguageSelect('en')}
              color={language === 'en' ? 'primary' : 'default'}
              size="small"
            >
              {language === 'en' && <CheckIcon fontSize="small" sx={{ mr: 0.5 }} />}
              EN
            </IconButton>
          </Tooltip>
          <Tooltip title="Română" arrow>
            <IconButton 
              onClick={() => handleLanguageSelect('ro')}
              color={language === 'ro' ? 'primary' : 'default'}
              size="small"
            >
              {language === 'ro' && <CheckIcon fontSize="small" sx={{ mr: 0.5 }} />}
              RO
            </IconButton>
          </Tooltip>
        </LanguageOptions>
      </Fade>
      
      <ButtonsContainer>
        <Tooltip title={t('nav.language')}>
          <StyledIconButton 
            onClick={handleLanguageClick}
            size="small"
            aria-label="Change language"
          >
            <LanguageIcon fontSize="small" />
          </StyledIconButton>
        </Tooltip>
        
        <Tooltip title={t('theme.toggle')}>
          <StyledIconButton 
            onClick={onToggleTheme}
            size="small"
            aria-label="Toggle dark mode"
          >
            {darkMode ? <LightIcon fontSize="small" /> : <DarkIcon fontSize="small" />}
          </StyledIconButton>
        </Tooltip>
      </ButtonsContainer>
    </SwitcherContainer>
  );
};

export default ThemeLanguageSwitcher; 